import { Avatar, Indicator, Tooltip } from "@mantine/core";
import { NavLink, useLocation } from "react-router";
import styled from "styled-components";
import { useState } from "react";

interface SidebarItemProps {
  navigateTo?: string;
  title: string;
  iconSrc?: string;
  icon?: React.ReactNode;
  isCollapsible?: boolean;
  subItems?: { title: string; navigateTo: string; badge?: number }[];
  sidebarOpen?: boolean;
  /**
   * Bekleyen iş sayısı (plan 2.59). Simgenin köşesindeki turuncu noktayı
   * yakar — sayı yazmaz, çünkü menü kapalıyken (yalnızca simge) sayının
   * yeri yok. Kesin sayı alt maddenin rozetinde ve ana sayfa kartında.
   */
  badge?: number;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  navigateTo = "/",
  title,
  isCollapsible,
  subItems = [],
  iconSrc,
  icon,
  sidebarOpen = true,
  badge = 0,
}) => {
  const location = useLocation();
  const isCurrentPathActive = location.pathname.startsWith(navigateTo);
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    if (isCollapsible) {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    }
    if (location.pathname === navigateTo) {
      e.preventDefault();
    }
  };

  return (
    <Tooltip label={title} position="right" disabled={sidebarOpen} withArrow>
      <ItemWrapper $isActive={isCurrentPathActive} $sidebarOpen={sidebarOpen}>
        <StyledItemLink onClick={handleClick} to={navigateTo}>
          {({ isActive }) => (
            <NavContentWrapper $sidebarOpen={sidebarOpen}>
              <Indicator color="#F97316" size={10} withBorder disabled={badge <= 0} position="bottom-start">
                {icon ? (
                    <div style={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", color: isActive ? "#5C26FF" : "#4C4C4C" }}>
                    {icon}
                    </div>
                ) : (
                    <Avatar
                    src={iconSrc}
                    style={{
                        filter: isActive
                        ? "brightness(0) saturate(100%) invert(16%) sepia(100%) saturate(3046%) hue-rotate(251deg) brightness(108%) contrast(118%)"
                        : "",
                    }}
                    size={20}
                    radius={0}
                    />
                )}
                </Indicator>

              {sidebarOpen && (
                <span style={{ fontSize: "16px", color: isActive ? "#5C26FF" : "#4C4C4C", whiteSpace: "nowrap" }}>
                  {title}
                </span>
              )}

              {isCollapsible && sidebarOpen && (
                <ChevronWrapper $isOpen={isOpen}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="#4C4C4C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </ChevronWrapper>
              )}
            </NavContentWrapper>
          )}
        </StyledItemLink>

        {isCollapsible && sidebarOpen && isOpen && (
          <SubitemsWrapper>
            {subItems.map((item) => (
              <StyledSubitemLink
                to={`${navigateTo}/${item.navigateTo}`}
                key={item.navigateTo}
            >
                {item.title}
                {!!item.badge && item.badge > 0 && (
                  <SubitemBadge>{item.badge}</SubitemBadge>
                )}
            </StyledSubitemLink>
            ))}
          </SubitemsWrapper>
        )}
      </ItemWrapper>
    </Tooltip>
  );
};

export default SidebarItem;

const StyledItemLink = styled(NavLink)`
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  text-decoration: none;
  display: flex;
  width: 100%;
  align-items: center;
  border-radius: 12px;
  &:hover > div {
    background-color: #eef2ff;
  }
  &.active > div {
    background-color: #eef2ff;
  }
`;

const StyledSubitemLink = styled(NavLink)`
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-decoration: none;
  width: 100%;
  padding: 6px 12px;
  border-radius: 8px;
  display: block;
  color: #4C4C4C;
  &:hover {
    background-color: #eef2ff;
  }
  &.active {
    background-color: #eef2ff;
    color: #5C26FF;
    font-weight: 600;
  }
`;

const SubitemBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 50px;
  background: #F97316;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  /* Satır yüksekliği kutuya eşit: rakamın tepesi kırpılmasın. */
  line-height: 18px;
  text-align: center;
  vertical-align: middle;
`;

const SubitemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 4px 16px 8px 48px;
  gap: 4px;
`;

const NavContentWrapper = styled.div<{ $sidebarOpen?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$sidebarOpen ? "flex-start" : "center")};
  gap: 12px;
  width: 100%;
  height: 44px;
  border-radius: 99px;
  padding: ${(p) => (p.$sidebarOpen ? "12px 16px" : "0px")};
  transition: background-color 0.3s ease;
`;

const ItemWrapper = styled.div<{ $isActive?: boolean; $sidebarOpen?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 12px;
  background-color: transparent;
  transition: background-color 0.2s ease;
  
`;

const ChevronWrapper = styled.div<{ $isOpen: boolean }>`
  margin-left: auto;
  display: flex;
  align-items: center;
  transform: rotate(${(p) => (p.$isOpen ? "180deg" : "0deg")});
  transition: transform 0.25s ease;
`;